import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdmin } from '@/hooks/useAdmin';
import { OwnershipManagement } from './components/OwnershipManagement';
import { SecurityCenter } from './components/SecurityCenter';
import { WhitelistManagement } from './components/WhitelistManagement';
import { ReferralManagement } from './components/ReferralManagement';
import { ContractManagement } from './components/ContractManagement';
import { NFTContractManagement } from './components/NFTContractManagement';
import { MultisenderContractManagement } from './components/MultisenderContractManagement';
import { BurnPageSettings } from './components/BurnPageSettings';
import { ToolsPageSettings } from './components/ToolsPageSettings';
import { LockerPageSettings } from './components/LockerPageSettings';
import { LiquidityPageSettings } from './components/LiquidityPageSettings';
import { Analytics } from './components/Analytics';
import { TokenList } from './components/TokenList';
import { TokenMonitoring } from './components/TokenMonitoring';
import { NetworkManagement } from './components/NetworkManagement';
import { ReportingDashboard } from './components/ReportingDashboard';
import { TierManagement } from './components/TierManagement';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FeeSetter } from './components/FeeSetter';
import { DollarSign } from 'lucide-react';

export default function AdminPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isInitialized } = useAdmin();

  useEffect(() => {
    // If we're initialized and not admin, redirect
    if (isInitialized && !isAdmin) {
      console.log('Not admin, redirecting to home');
      navigate('/');
    }
  }, [isAdmin, isInitialized, navigate]);

  // Show loading spinner while initializing
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Don't render anything if not admin
  if (!isAdmin) {
    return null;
  }

  // Get the current hash from the URL
  const hash = location.hash || '#analytics';

  const setHash = (newHash: string) => {
    navigate({ hash: newHash }, { replace: true });
  };

  return (
    <div className="container mx-auto p-4 space-y-4">
      <div className="flex flex-wrap gap-2 justify-center">
        <Button
          variant={hash === '#analytics' ? 'default' : 'outline'}
          onClick={() => setHash('analytics')}
        >
          Analytics
        </Button>
        <Button
          variant={hash === '#tokens' ? 'default' : 'outline'}
          onClick={() => setHash('tokens')}
        >
          Tokens
        </Button>
        <Button
          variant={hash === '#contract' ? 'default' : 'outline'}
          onClick={() => setHash('contract')}
        >
          Contract
        </Button>
        <Button
          variant={hash === '#nftcontract' ? 'default' : 'outline'}
          onClick={() => setHash('nftcontract')}
        >
          NFT Contract
        </Button>
        <Button
          variant={hash === '#mscontract' ? 'default' : 'outline'}
          onClick={() => setHash('mscontract')}
        >
          MS Contract
        </Button>
        <Button
          variant={hash === '#feesetter' ? 'default' : 'outline'}
          onClick={() => setHash('feesetter')}
          className="flex items-center gap-2"
        >
          <DollarSign className="h-4 w-4 text-green-500" />
          Fee Settings
        </Button>
      </div>

      {/* Render the appropriate component based on the hash */}
      {hash === '#analytics' && <Analytics />}
      {hash === '#tokens' && <TokenList />}
      {hash === '#contract' && <ContractManagement />}
      {hash === '#nftcontract' && <NFTContractManagement />}
      {hash === '#mscontract' && <MultisenderContractManagement />}
      {hash === '#feesetter' && <FeeSetter />}
    </div>
  );
}
